import { useState, useEffect } from 'react'
import {
  Button,
  Input,
  Grid,
  GridItem,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  NativeSelectRoot,
  NativeSelectField,
} from '@chakra-ui/react'
import { User, Save } from 'lucide-react'
import { Field } from '@/components/ui/field'
import {
  useCreateExternalContact,
  useUpdateExternalContact,
} from '../hooks/useExternalContacts'
import type { ExternalContact, ContactType, NotificationPreference } from '../types'
import { CONTACT_TYPE_OPTIONS, NOTIFICATION_PREFERENCE_OPTIONS } from '../types'

interface ExternalContactModalProps {
  isOpen: boolean
  onClose: () => void
  residenceId: string
  contact?: ExternalContact | null
  onSuccess?: (contact: ExternalContact) => void
}

type FormData = {
  first_name: string
  last_name: string
  civility: string
  contact_type: ContactType
  email: string
  phone: string
  address: string
  notification_email: string
  notification_preference: NotificationPreference
}

const initialFormData: FormData = {
  first_name: '',
  last_name: '',
  civility: '',
  contact_type: 'mandataire',
  email: '',
  phone: '',
  address: '',
  notification_email: '',
  notification_preference: 'ELECTRONIC',
}

export function ExternalContactModal({
  isOpen,
  onClose,
  residenceId,
  contact,
  onSuccess,
}: ExternalContactModalProps) {
  const isEditMode = !!contact
  const createContact = useCreateExternalContact()
  const updateContact = useUpdateExternalContact()

  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({})

  useEffect(() => {
    if (isOpen && contact) {
      setFormData({
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        civility: contact.civility || '',
        contact_type: contact.contact_type || 'mandataire',
        email: contact.email || '',
        phone: contact.phone || '',
        address: contact.address || '',
        notification_email: contact.notification_email || '',
        notification_preference: contact.notification_preference || 'ELECTRONIC',
      })
    } else if (isOpen && !contact) {
      setFormData(initialFormData)
    }
    setErrors({})
  }, [isOpen, contact])

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {}

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'Le prénom est requis'
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Le nom est requis'
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide'
    }
    if (formData.notification_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.notification_email)) {
      newErrors.notification_email = 'Email de notification invalide'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    try {
      if (isEditMode && contact) {
        const result = await updateContact.mutateAsync({
          id: contact.id,
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          civility: formData.civility || null,
          contact_type: formData.contact_type,
          email: formData.email || null,
          phone: formData.phone || null,
          address: formData.address || null,
          notification_email: formData.notification_email || null,
          notification_preference: formData.notification_preference,
        })
        onSuccess?.(result)
      } else {
        const result = await createContact.mutateAsync({
          residence_id: residenceId,
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          civility: formData.civility || null,
          contact_type: formData.contact_type,
          email: formData.email || null,
          phone: formData.phone || null,
          address: formData.address || null,
          notification_email: formData.notification_email || null,
          notification_preference: formData.notification_preference,
        })
        onSuccess?.(result)
      }
      onClose()
    } catch (error) {
      console.error('Error saving contact:', error)
    }
  }

  const isLoading = createContact.isPending || updateContact.isPending

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && onClose()} size="lg">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="600px">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle display="flex" alignItems="center" gap={2}>
              <User size={20} />
              {isEditMode ? 'Modifier le contact' : 'Nouveau contact externe'}
            </DialogTitle>
          </DialogHeader>

          <DialogBody>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem>
                <Field label="Civilité">
                  <NativeSelectRoot size="sm">
                    <NativeSelectField
                      value={formData.civility}
                      onChange={(e) => handleChange('civility', e.target.value)}
                    >
                      <option value="">Sélectionner...</option>
                      <option value="M.">M.</option>
                      <option value="Mme">Mme</option>
                      <option value="Me">Me</option>
                      <option value="Dr">Dr</option>
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              </GridItem>
              <GridItem>
                <Field label="Type de contact" required>
                  <NativeSelectRoot size="sm">
                    <NativeSelectField
                      value={formData.contact_type}
                      onChange={(e) => handleChange('contact_type', e.target.value)}
                    >
                      {CONTACT_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              </GridItem>

              <GridItem>
                <Field label="Prénom" required error={errors.first_name}>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    placeholder="Prénom"
                    size="sm"
                  />
                </Field>
              </GridItem>
              <GridItem>
                <Field label="Nom" required error={errors.last_name}>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    placeholder="Nom"
                    size="sm"
                  />
                </Field>
              </GridItem>

              <GridItem>
                <Field label="Email" error={errors.email}>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="email@exemple.com"
                    size="sm"
                  />
                </Field>
              </GridItem>
              <GridItem>
                <Field label="Téléphone">
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="01 23 45 67 89"
                    size="sm"
                  />
                </Field>
              </GridItem>

              <GridItem colSpan={2}>
                <Field label="Adresse">
                  <Input
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    placeholder="Adresse complète"
                    size="sm"
                  />
                </Field>
              </GridItem>

              <GridItem colSpan={2} borderTopWidth="1px" pt={4} mt={2}>
                <Field label="Préférence de notification" helperText="Mode de réception des convocations AG">
                  <NativeSelectRoot size="sm">
                    <NativeSelectField
                      value={formData.notification_preference}
                      onChange={(e) => handleChange('notification_preference', e.target.value)}
                    >
                      {NOTIFICATION_PREFERENCE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              </GridItem>

              {formData.notification_preference === 'ELECTRONIC' && (
                <GridItem colSpan={2}>
                  <Field
                    label="Email de notification"
                    helperText="Laissez vide pour utiliser l'email principal"
                    error={errors.notification_email}
                  >
                    <Input
                      type="email"
                      value={formData.notification_email}
                      onChange={(e) => handleChange('notification_email', e.target.value)}
                      placeholder="notification@exemple.com"
                      size="sm"
                    />
                  </Field>
                </GridItem>
              )}
            </Grid>
          </DialogBody>

          <DialogFooter>
            <Button variant="ghost" onClick={onClose} disabled={isLoading}>
              Annuler
            </Button>
            <Button
              colorPalette="brand"
              onClick={handleSubmit}
              loading={isLoading}
            >
              <Save size={16} />
              {isEditMode ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
